import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/services/api.client'

export interface GenelAyar {
  plasiyerKullan: boolean
  plasiyerKendiCarileriniGorsun: boolean
  satirBazliDepo: boolean
  cokluDepoTahsis: boolean
  siparisOnayAktif: boolean
  siparisOnaycilar: string[]
  siparisRezervasyon: boolean
  subeBazliCalisma: boolean
  cokluParaBirimi: boolean
  otomatikKurGuncelle: boolean
  otomatikKurSaati: string
  kurTipi: string
  servisModulu: boolean
}

const DEFAULT_AYAR: GenelAyar = { plasiyerKullan: false, plasiyerKendiCarileriniGorsun: false, satirBazliDepo: false, cokluDepoTahsis: false, siparisOnayAktif: false, siparisOnaycilar: [], siparisRezervasyon: false, subeBazliCalisma: false, cokluParaBirimi: false, otomatikKurGuncelle: true, otomatikKurSaati: '10:05', kurTipi: 'doviz_alis', servisModulu: false }

export const GENEL_AYAR_KEY = ['genel-ayar'] as const

async function fetchGenelAyar(): Promise<GenelAyar> {
  const r = await (apiClient.get('/settings/genel') as Promise<unknown>)
  if (r && typeof r === 'object' && 'plasiyerKullan' in r) return r as GenelAyar
  if (r && typeof r === 'object' && 'data' in r) return (r as any).data as GenelAyar
  return DEFAULT_AYAR
}

export function useGenelAyar() {
  return useQuery({
    queryKey: GENEL_AYAR_KEY,
    queryFn: fetchGenelAyar,
    staleTime: 5 * 60 * 1000,
    placeholderData: DEFAULT_AYAR,
  })
}

export function usePlasiyerKullan(): boolean {
  const { data } = useGenelAyar()
  return data?.plasiyerKullan ?? false
}

/** Belge kalemlerinde satır bazında depo seçimi açık mı? (firma-geneli) */
export function useSatirBazliDepo(): boolean {
  const { data } = useGenelAyar()
  return data?.satirBazliDepo ?? false
}

/** Çoklu-depo tahsis yardımcısı açık mı? (satır bazı depoya bağlı, firma-geneli) */
export function useCokluDepoTahsis(): boolean {
  const { data } = useGenelAyar()
  return (data?.satirBazliDepo ?? false) && (data?.cokluDepoTahsis ?? false)
}

/** Sipariş rezervasyonu açık mı? (firma-geneli) — sipariş ekranındaki rezerve checkbox'ını yönetir */
export function useSiparisRezervasyon(): boolean {
  const { data } = useGenelAyar()
  return data?.siparisRezervasyon ?? false
}

/** Şube ekseni ana şalteri açık mı? (Faz 1b) — şube-özel davranışları yönetir (POS depo/kasa/banka) */
export function useSubeBazliCalisma(): boolean {
  const { data } = useGenelAyar()
  return data?.subeBazliCalisma ?? false
}

/** Çoklu para birimi ana şalteri açık mı? (Faz M0) — döviz belge/kur, per-döviz cari bakiye, kur farkı */
export function useCokluParaBirimi(): boolean {
  const { data } = useGenelAyar()
  return data?.cokluParaBirimi ?? false
}

/** Teknik servis / arıza takibi modülü açık mı? — Servis menüsü + ekranlarını yönetir */
export function useServisModulu(): boolean {
  const { data } = useGenelAyar()
  return data?.servisModulu ?? false
}

export function useGenelAyarGuncelle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: Partial<GenelAyar>) =>
      (apiClient.put('/settings/genel', dto) as Promise<unknown>),
    onMutate: async (dto) => {
      await qc.cancelQueries({ queryKey: [...GENEL_AYAR_KEY] })
      const prev = qc.getQueryData<GenelAyar>([...GENEL_AYAR_KEY])
      qc.setQueryData([...GENEL_AYAR_KEY], { ...DEFAULT_AYAR, ...prev, ...dto })
      return { prev }
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData([...GENEL_AYAR_KEY], ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: [...GENEL_AYAR_KEY] })
    },
  })
}
